<template>
  <v-container fluid justify-start>
    
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="facilities"
      :search="search"
      :custom-filter="customFilter"
      :loading="isLoading"
      item-key="id"
      :no-data-text="noDataText"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense flat dark :color="appColor_Medium">
          
          <app-back-button v-bind:color="appColor_Dark" />

          <!--<v-icon large class="mr-2">domain</v-icon>-->
          <v-toolbar-title>Facilities</v-toolbar-title>

          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog
              v-model="editDialog"
              max-width="400px"
              :persistent="true"
              >
              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice" slot="activator" small :disabled="isLoading" :color="appColor_Dark" dark class="mb-1 mt-0 mr-0 elevation-0" v-on="on">New</v-btn>
                <v-btn v-else slot="activator" :color="appColor_Dark" :disabled="isLoading" class="mb-1 mt-0 mr-0 elevation-0" v-on="on">Create Facility</v-btn>
              </template>
              <v-card>
                <v-toolbar dense dark flat :color="appColor_Medium">
                  <v-toolbar-title><v-icon>edit</v-icon> {{ formTitle }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="pa-0 pt-2 ma-0">
                  <v-container grid-list-md class="pt-0 pb-0">
                    <form>
                      <v-layout wrap>
                        <v-flex xs12 sm12 md12>
                          <v-text-field
                            v-model="editedFacility.name"
                            label="Name"
                            v-validate="'required|max:50'"
                            :error-messages="errors.collect('name')"
                            data-vv-name="name"
                            ref="name"
                          />
                          <v-text-field
                          v-model="editedFacility.street"
                            label="Street"
                            v-validate="'max:100'"
                            :error-messages="errors.collect('street')"
                            data-vv-name="street"
                          />
                          <v-text-field
                            v-model="editedFacility.city"
                            label="City"
                            v-validate="'max:100'"
                            :error-messages="errors.collect('city')"
                            data-vv-name="city"
                          />
                          <v-select
                            v-if="showProvState"
                            v-model="editedFacility.provStateId"
                            item-value="id"
                            item-text="description"
                            :items="filteredProvStates"
                            :label="provStateLabel"
                            :placeholder="provStatePlaceholder"
                            @change="provStateChanged"
                            clearable
                          />
                          <v-select
                            v-model="editedFacility.countryId"
                            item-value="id"
                            item-text="description"
                            :items="countries"
                            label="Country"
                            placeholder="Select country"
                            @change="countryChanged"
                            clearable
                          />
                          <domain-combobox
                            v-model="editedFacility.domainId"
                            :options="domainOptions"
                            :label="'Domain'"
                          />
                        </v-flex>
                      </v-layout>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions>

                  <v-spacer />
                  
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="appColor_Dark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>

              </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
        <v-row no-gutters class="mx-3 mt-2">

          <!-- Search -->
          <v-col cols="12" md="12">
            <v-text-field
              v-model="search"
              append-icon="search "
              label="Search"
              single-line
              hide-details
              clearable
              >
            </v-text-field>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left">
          <router-link to="/facilityManager">
            <span @click="edit(item)">{{ item.name }}</span>
          </router-link>
        </span>
      </template>

      <template v-slot:item.createDate="{ item }">
        <span class="text-left">
          <app-date :date="item.createDate" :format="'MMM-DD-YYYY'" />
        </span>
      </template>
      
      <!-- <template v-slot:item.domain="{ item }">
        <div class="text-left" v-html="item.domainName"></div>
      </template> -->

      <template v-slot:item.action="{ item }">
        <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import AppDate from '@/components/App/AppDate'
import AppBackButton from '@/components/App/AppBackButton'
import appService from '@/app.service'
import { AppColor, CountryId } from '@/config.js'
import DomainCombobox from '@/components/DomainCombobox'

export default {
  name: 'FacilityManager',
  components: {
    AppDate,
    AppBackButton,
    DomainCombobox
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {

      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Toolbar: AppColor.Toolbar,
      appColor_DeleteButton: AppColor.DeleteButton,
      appColor_Progress: AppColor.Progress,
      appColor_Warning: AppColor.Warning,
      appColor_Delete: AppColor.DeleteButton,
      
      // countries
      countryId_Canada: CountryId.Canada,

      isLoading: true,
      isSaving: false,
      editDialog: false,
      isDeleting: false,
      search: '',
      facilities: [],
      domains: [],
      domainOptions: [],
      provStates: [],
      countries: [],

      headers: [
        {
          text: 'Name',
          value: 'name',
          align: 'left',
          width: '25%',
          class: 'pr-0'
        },
        {
          text: 'City',
          value: 'city',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Prov/State',
          value: 'provState',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          text: 'Domain',
          value: 'domainName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Created',
          value: 'createDate',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          value: 'action',
          sortable: false,
          align: 'center',
          width: '5%'
        }
      ],
      editedIndex: -1,
      editedFacility: {
        id: -1,
        name: null,
        domainId: null,
        street: null,
        city: null,
        provStateId: null,
        countryId: null,
        createDate: null
      },
      defaultFacility: {
        id: -1,
        name: null,
        domainId: null,
        street: null,
        city: null,
        provStateId: null,
        countryId: null,
        createDate: null
      },
      restoreFacility: {
        id: -1,
        name: null,
        domainId: null,
        street: null,
        city: null,
        provStateId: null,
        countryId: null,
        createDate: null
      },
      dictionary: {
        custom: {
          name: {
            required: () => 'Name is required',
            max: 'Name must be 50 characters or less'
          },
          street: {
            max: 'Street cannot exceed 100 characters'
            },
          city: {
            max: 'City cannot exceed 100 characters'
          }
        }
      },
    }
  },
  computed: {
    noDataText () {
      if (this.isLoading) return 'One moment...'

      return this.facilities.length === 0
        ? 'No facilities found'
        : ''
    },
    formTitle () {
      return this.editedFacility.id < 0 ? 'New Facility' : 'Edit Facility'
    },
    filteredProvStates () {
      const self = this
      if (self.editedFacility.countryId === null) return []
      return self.provStates.filter(ps => ps.countryId === self.editedFacility.countryId)
    },
    provStateLabel () {
      if (this.editedFacility.countryId === null) return 'Prov/State'
      return this.editedFacility.countryId === this.countryId_Canada
        ? 'Province'
        : 'State'
    },
    provStatePlaceholder () {
      if (this.editedFacility.countryId === null) return ''
      return this.editedFacility.countryId === this.countryId_Canada
        ? 'Select province'
        : 'Select state'
    },
    showProvState () {
      return this.editedFacility.countryId !== null
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true
      appService.getFacilitiesForManager().then(data => {
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.domains = data.domains
          self.provStates = data.provStates
          self.countries = data.countries
          self.facilities = data.facilities.map(f => { return {
            id: f.id,
            name: f.name,
            street: f.street,
            city: f.city,
            provStateId: f.provStateId,
            provState: this.getProvState(f),
            countryId: f.countryId,
            domainId: f.domainId,
            domainName: this.getDomainName(f.domainId),
            createDate: f.createDate
          }})

          // domains for combo
          self.domainOptions = self.domains.map(d => { return {
            id: d.id,
            name: d.name,
            organizationName: d.organizationName
          }})
          self.domainOptions.unshift({ id: 0, name: '(No Domain)', organizationName: null })
        }
        self.isLoading = false
      }).catch(e => { self.handleCriticalError(e) })
    },

    edit (item) {
      const self = this
      self.editedIndex = self.facilities.findIndex(f => f.id === item.id)
      self.editedFacility = Object.assign({}, item)
      self.restoreFacility = Object.assign({}, item)
      
      self.editDialog = true
      setTimeout(() => {
        self.$refs.name.focus()
      }, 100)
    },

    remove (item) {
      const self = this
      const index = self.facilities.findIndex(f => f.id === item.id)
      if (index < 0) return
      
      const name = self.facilities[index].name

      self.$confirm.show({
        title: 'Delete Facility?',
        text: `<b>${name}</b>`,
        onConfirm: () => {
          self.isDeleting = true
          self.validateDelete(item.id, name).then(result => {
            if (result.errorSeverity !== null) {
              self.isDeleting = false
              self.handleError(result)
            } else {
              appService.deleteFacility(item.id).then((data) => {
                if (data.errorSeverity > 0) {
                  self.handleError(data)
                } else {
                  self.facilities.splice(index, 1)
                  self.updateDomainsList(data.facility)
                }
                self.isDeleting = false
              }).catch(e => { self.handleCriticalError(e) })
            }
          })
        }
      })
    },

    save () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          const isEditMode = self.editedIndex > -1
          if (isEditMode) {
            var editedFacility = self.facilities[self.editedIndex]
            self.confirmEdit(editedFacility).then(result => {
              if (result) {
                self.isSaving = true
                Object.assign(editedFacility, self.editedFacility)
                const facilityModel = self.getModel(editedFacility)
                appService.updateFacility(editedFacility.id, facilityModel).then((data) => {
                  if (data.errorSeverity !== null) {
                    self.handleError(data)
                    Object.assign(self.facilities[self.editedIndex], self.restoreFacility)
                  } else {
                    self.facilities[self.editedIndex].provState = self.getProvState(data.facility)
                    self.facilities[self.editedIndex].domainName = self.getDomainName(data.facility.domainId)
                    self.facilities[self.editedIndex].domainId = data.facility.domainId,
                    self.updateDomainsList(data.facility)
                    self.closeDialog()
                  }
                  self.isSaving = false
                })
              }
            })
          } else {
            appService.addFacility(self.getModel(self.editedFacility)).then((data) => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.editedFacility.id = data.facility.id
                self.editedFacility.createDate = data.facility.createDate
                self.editedFacility.provState = self.getProvState(data.facility)
                self.editedFacility.domainName = self.getDomainName(data.facility.domainId)
                self.editedFacility.domainId = data.facility.domainId
                self.facilities.unshift(self.editedFacility)
                self.updateDomainsList(data.facility)
              }
              self.isSaving = false
              self.closeDialog()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    getModel (f) {
      const domainId = f.domainId > 0
        ? f.domainId
        : null

      return {
        id: f.id,
        name: f.name,
        domainId: domainId,
        street: f.street,
        city: f.city,
        provStateId: f.provStateId,
        countryId: f.countryId
      }
    },
    
    getProvState (f) {
      var code = null
      
      if (f.provStateId > 0) {
        const provStateCodes = this.provStates.filter(ps => ps.id === f.provStateId)
        code = provStateCodes.length > 0
          ? provStateCodes[0].code
          : null
      }

      return code
    },

    updateDomainsList (facility) {
      // remove facilityId from old domain
      const oldDomain = this.domains.find(d => (d.facilityIds ?? []).includes(facility.id))
      if (oldDomain) {
        const facilityIndex = oldDomain.facilityIds.indexOf(facility.id)
        oldDomain.facilityIds.splice(facilityIndex, 1)
      }
      
      // add facilityId to new domain
      const newDomain = this.domains.find(d => d.id === facility.domainId)
      if (newDomain) {
        if (!newDomain.facilityIds) {
          newDomain.facilityIds = [facility.id]
        } else if (!newDomain.facilityIds.includes(facility.id)) {
          newDomain.facilityIds.push(facility.id)
        }
      }
    },

    getDomainName (domainId) {
      if (domainId === null) return null
      const domains = this.domains.filter(d => d.id === domainId)
      return domains.length > 0
        ? domains[0].name
        : null
    },

    confirmEdit (editedFacility) {
      const self = this

      return new Promise((resolve) => {
        // check if a DM will be left without a domain
        if (editedFacility.domainId !== null 
        && editedFacility.domainId !== self.editedFacility.domainId) {
          const index = self.domains.findIndex(d => d.id === editedFacility.domainId)
          if (index >= 0) {
            const domainId = self.domains[index].id
            const domainName = self.domains[index].name
            self.validateDomainManagers (domainId, domainName).then(data => {
              if (data.errorSeverity > 0) {
                this.handleError(data)
                resolve(false)
              }
              else if (data.errorSeverity === 0) {
                self.$confirm.show({
                  title: 'Change Domains?',
                  text: data.errorMessage,
                  formColor: self.appColor_Warning,
                  onConfirm: () => resolve(true),
                  onCancel: () => resolve(false)
                })
              } else {
                resolve(true)
              }
            })
          } else {
            this.handleError({ errorSeverity: 1, errorMessage: 'No internal domain found'})
            resolve(false)
          }
        } else {
          resolve(true)
        }
      })
    },

    validateDelete (id, name) {
      return new Promise((resolve) => {
        const self = this

        self.validateKiosks(id, name).then(data => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            self.validateDomainFacility(id, name).then(data => {
              resolve(data)
            })
          }
        })
      })
    },

    validateKiosks (id, name) {
      return new Promise (resolve => {
        
        appService.getFacilityKiosks(id).then((data) => {
          if (data.errorSeverity > 0) {
            resolve(data)
          } else {
            if (data.kiosks.length === 0) {
              resolve({ errorSeverity: null })
            }
              const kiosks = data.kiosks
              var list = ''

              kiosks.forEach((k) => {
                list += `${k.serialNumber}<br/>`
              })

              // pluralize
              const result =
                kiosks.length === 1
                  ? `<b>${name}</b> still hosts a kiosk:<br/><br/>` +
                    list +
                    '<br/>Delete or move the kiosk to another facility before attempting to delete this facility.'
                  : `<b>${name}</b> is hosting ${kiosks.length} kiosks:<br/><br/>` +
                    list +
                    '<br/>Delete or move the above kiosks to another facility before attempting to delete this facility.'

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    validateDomainFacility (facilityId, facilityName) {
      return new Promise (resolve => {
        const self = this

        const domains = self.domains.filter(d => (d.facilityIds ?? []).includes(facilityId))
        if (domains.length === 0) resolve({ errorSeverity: null })

        const domainId = domains[0].id
        appService.getDomain(domainId).then(data => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else if (data.domain.facilityIds.includes(facilityId)) {
            const result =
              `<b>${facilityName}</b> is still associated with a domain:<br/><br/>` +
              data.domain.name +
              `<br/><br/>You must unassign <b>${facilityName}</b> from the above domain before attempting to delete this facility.`

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          } else {
            resolve({ errorSeverity: null })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    validateDomainManagers (id, name) {
      return new Promise (resolve => {
        const self = this
        const index = self.domains.findIndex((d) => d.id === id)
        self.isSaving = true
        appService.getDomainManagers(self.domains[index].id).then(data => {
          self.isSaving = false
          if (data.errorSeverity > 0) {
            resolve(data)
          } else {
            if (data.users.length === 0) {
              resolve({ errorSeverity: null })
            }
            const users = data.users
            const result = users.length === 1
              ? `The domain <b>${name}</b> has a <i>${self.getExpression('FunctionalRole', 'DomainManager')}</i> assigned to it.<br/><br/>`
                + `Switching this facility to a different domain will affect the Domain Manager.<br/><br/>`
                + 'Would you like to continue?'
              : `The domain <b>${name}</b> has ${users.length} <i>Domain Managers</i> assigned to it.<br/><br/>`
                + `Switching this facility to a different domain will affect these Domain Managers.<br/><br/>`
                + 'Would you like to continue?'

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    closeDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.resetEditedFacility()
      }, 300)
    },

    resetEditedFacility () {
      this.editedFacility = Object.assign({}, this.defaultFacility)
      this.editedIndex = -1
      this.$validator.reset()
    },

    provStateChanged (e) {
      this.editedFacility.provStateId = e !== undefined
        ? e
        : null
    },

    countryChanged (e) {
      const value = e !== undefined
        ? e
        : null
      
      if (value === null) {
        this.editedFacility.countryId = null
        this.editedFacility.provStateId = null
      }
    },

    customFilter (value, search, item) {
      const city  = item.city ?? ''
      const provState  = item.provState ?? ''
      const domainName  = item.domainName ?? ''

      return value != null &&
        search != null &&
        (item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        domainName.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        city.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        provState.toLowerCase().indexOf(search.toLowerCase()) !== -1)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
                      ? 'Validation Failed'
                      : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
  },
  watch: {
    editDialog (val) {
      if (val) {
        const self = this
        setTimeout(() => {
          // set focus to name input in the edit dialog
          self.$refs.name.focus()
        }, 300)
      }
    }
  }
}
</script>
